<template>
  <div class="dicts-editor">

    <!-- Заголовок + подвкладки -->
    <div class="dicts-header">
      <div class="dicts-header__left">
        <span class="dicts-header__title">Справочники</span>
        <span class="dicts-header__sub">Управление типами задач и авто-действиями</span>
      </div>
      <div class="dicts-subtabs">
        <button
          v-for="t in dictTabs"
          :key="t.id"
          :class="['subtab', { 'subtab--active': activeDict === t.id }]"
          @click="activeDict = t.id"
        >
          <span class="subtab__icon">{{ t.icon }}</span>
          {{ t.label }}
          <span class="subtab__count">{{ t.id === 'task_types' ? dicts.taskTypes.length : dicts.autoActions.length }}</span>
        </button>
      </div>
    </div>

    <!-- Контент -->
    <div class="dicts-body">

      <!-- Плашка-подсказка -->
      <div v-if="activeDict === 'task_types'" class="info-banner">
        <span class="info-banner__icon">ℹ</span>
        <div>
          Код задачи (<code>task_type_code</code>) задаётся один раз при создании и не меняется.
          Именно этот код используется в BPMN-схемах и вызовах <code>handle_event</code>.
        </div>
      </div>
      <div v-else class="info-banner info-banner--warn">
        <span class="info-banner__icon">⚠</span>
        <div>
          После добавления нового авто-действия необходимо добавить ветку
          <code>elsif auto_action_code = '...'</code> в функцию
          <code>wcheck.bpmn_process__handle_event</code> в БД.
        </div>
      </div>

      <!-- Тулбар -->
      <div class="table-toolbar">
        <button class="btn-add" @click="openAdd(activeDict === 'task_types' ? 'task_type' : 'auto_action')">
          <span>＋</span> Добавить
        </button>
        <div v-if="error" class="toolbar-error">{{ error }}</div>
      </div>

      <!-- Таблица типов задач -->
      <template v-if="activeDict === 'task_types'">
        <div class="table-wrap">
          <table class="dict-table">
            <thead>
              <tr>
                <th style="width:220px">Код</th>
                <th>Название</th>
                <th style="width:110px">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!dicts.taskTypes.length">
                <td colspan="3" class="table-empty">Нет данных</td>
              </tr>
              <tr v-for="row in dicts.taskTypes" :key="row.value">
                <td><code class="code-cell">{{ row.value }}</code></td>
                <td>{{ row.label }}</td>
                <td class="actions-cell">
                  <button class="act-btn act-btn--edit" title="Редактировать" @click="openEdit('task_type', row)">✏</button>
                  <button class="act-btn act-btn--del"  title="Удалить"       @click="confirmDelete('task_type', row)">🗑</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Таблица авто-действий -->
      <template v-else>
        <div class="table-wrap">
          <table class="dict-table">
            <thead>
              <tr>
                <th style="width:220px">Код</th>
                <th style="width:240px">Название</th>
                <th>Описание</th>
                <th style="width:110px">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!dicts.autoActions.length">
                <td colspan="4" class="table-empty">Нет данных</td>
              </tr>
              <tr v-for="row in dicts.autoActions" :key="row.value">
                <td><code class="code-cell">{{ row.value }}</code></td>
                <td>{{ row.label }}</td>
                <td class="desc-cell">{{ row.description || '—' }}</td>
                <td class="actions-cell">
                  <button class="act-btn act-btn--edit" title="Редактировать" @click="openEdit('auto_action', row)">✏</button>
                  <button class="act-btn act-btn--del"  title="Удалить"       @click="confirmDelete('auto_action', row)">🗑</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

    </div>

    <!-- ===== Модалка добавления / редактирования ===== -->
    <Transition name="modal">
      <div v-if="modal.open" class="modal-overlay" @click.self="closeModal">
        <div class="modal">
          <div class="modal__header">
            <div class="modal__title">
              <span class="modal__title-icon">{{ modal.isNew ? '＋' : '✏' }}</span>
              {{ modal.isNew ? 'Добавить' : 'Редактировать' }} {{ modal.typeLabel }}
            </div>
            <button class="modal__close" @click="closeModal">✕</button>
          </div>

          <div class="modal__body">
            <div v-if="modal.error" class="modal__error">{{ modal.error }}</div>

            <template v-if="modal.dictType === 'task_type'">
              <div class="field">
                <label class="field__label">Код <span class="field__req">*</span></label>
                <div class="field__hint">Латиница, snake_case — задаётся раз и не меняется</div>
                <input
                  v-model="modal.form.task_type_code"
                  class="field__input"
                  :class="{ 'field__input--disabled': !modal.isNew }"
                  :disabled="!modal.isNew"
                  placeholder="upload_geodesy"
                />
              </div>
              <div class="field">
                <label class="field__label">Название <span class="field__req">*</span></label>
                <input v-model="modal.form.task_type_name" class="field__input" placeholder="Загрузить геодезический акт" />
              </div>
            </template>

            <template v-else-if="modal.dictType === 'auto_action'">
              <div class="field">
                <label class="field__label">Код <span class="field__req">*</span></label>
                <div class="field__hint">Латиница, UPPER_CASE — задаётся раз и не меняется</div>
                <input
                  v-model="modal.form.auto_action_code"
                  class="field__input"
                  :class="{ 'field__input--disabled': !modal.isNew }"
                  :disabled="!modal.isNew"
                  placeholder="SEND_NOTIFICATION"
                />
              </div>
              <div class="field">
                <label class="field__label">Название <span class="field__req">*</span></label>
                <input v-model="modal.form.auto_action_name" class="field__input" placeholder="Отправить уведомление" />
              </div>
              <div class="field">
                <label class="field__label">Описание</label>
                <textarea v-model="modal.form.description" class="field__textarea" rows="3" placeholder="Что именно делает это авто-действие..." />
              </div>
              <div v-if="modal.isNew" class="reminder">
                📌 После сохранения добавьте обработку в <code>bpmn_process__handle_event</code>
              </div>
            </template>
          </div>

          <div class="modal__footer">
            <button class="btn-primary" :disabled="saving" @click="save">
              <span v-if="saving" class="btn-spinner">⟳</span>
              {{ saving ? 'Сохранение...' : modal.isNew ? 'Добавить' : 'Сохранить' }}
            </button>
            <button class="btn-ghost" @click="closeModal">Отмена</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ===== Подтверждение удаления ===== -->
    <Transition name="modal">
      <div v-if="deleteConfirm.open" class="modal-overlay" @click.self="deleteConfirm.open = false">
        <div class="modal modal--sm">
          <div class="modal__header">
            <div class="modal__title">
              <span class="modal__title-icon modal__title-icon--danger">🗑</span>
              Удалить запись?
            </div>
            <button class="modal__close" @click="deleteConfirm.open = false">✕</button>
          </div>
          <div class="modal__body">
            <p class="delete-label"><code>{{ deleteConfirm.label }}</code></p>
            <p class="delete-hint">Если запись используется в BPMN-схемах — удаление будет запрещено.</p>
            <div v-if="deleteConfirm.error" class="modal__error">{{ deleteConfirm.error }}</div>
          </div>
          <div class="modal__footer">
            <button class="btn-danger" :disabled="saving" @click="doDelete">
              {{ saving ? 'Удаление...' : 'Удалить' }}
            </button>
            <button class="btn-ghost" @click="deleteConfirm.open = false">Отмена</button>
          </div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useBpmnDicts } from './useBpmnDicts'
import { wcheckBpmnAPI } from './services/api'

const dicts = useBpmnDicts()
const saving = ref(false)
const error  = ref(null)
const activeDict = ref('task_types')

const dictTabs = [
  { id: 'task_types',   label: 'Типы задач',    icon: '📋' },
  { id: 'auto_actions', label: 'Авто-действия',  icon: '⚡' },
]

// --- Модалка ---
const modal = reactive({ open: false, isNew: true, dictType: null, typeLabel: '', form: {}, error: null })

function openAdd(dictType) {
  Object.assign(modal, {
    open: true, isNew: true, dictType,
    typeLabel: dictType === 'task_type' ? 'тип задачи' : 'авто-действие',
    error: null,
    form: dictType === 'task_type'
      ? { task_type_code: '', task_type_name: '' }
      : { auto_action_code: '', auto_action_name: '', description: '' },
  })
}

function openEdit(dictType, row) {
  Object.assign(modal, {
    open: true, isNew: false, dictType,
    typeLabel: dictType === 'task_type' ? 'тип задачи' : 'авто-действие',
    error: null,
    form: dictType === 'task_type'
      ? { task_type_id: row._id, task_type_code: row.value, task_type_name: row.label }
      : { bpmn_auto_action_id: row._id, auto_action_code: row.value, auto_action_name: row.label, description: row.description || '' },
  })
}

function closeModal() { modal.open = false }

async function save() {
  modal.error = null
  saving.value = true
  try {
    if (modal.dictType === 'task_type') {
      modal.isNew ? await wcheckBpmnAPI.createTaskType(modal.form) : await wcheckBpmnAPI.updateTaskType(modal.form)
    } else {
      modal.isNew ? await wcheckBpmnAPI.createAutoAction(modal.form) : await wcheckBpmnAPI.updateAutoAction(modal.form)
    }
    closeModal()
    await dicts.reload()
  } catch (e) {
    modal.error = e?.response?.data?.error || e?.message || 'Ошибка сохранения'
  } finally {
    saving.value = false
  }
}

// --- Удаление ---
const deleteConfirm = reactive({ open: false, dictType: null, id: null, label: '', error: null })

function confirmDelete(dictType, row) {
  Object.assign(deleteConfirm, {
    open: true, dictType, id: row._id,
    label: `${row.value} — ${row.label}`,
    error: null,
  })
}

async function doDelete() {
  deleteConfirm.error = null
  saving.value = true
  try {
    deleteConfirm.dictType === 'task_type'
      ? await wcheckBpmnAPI.deleteTaskType(deleteConfirm.id)
      : await wcheckBpmnAPI.deleteAutoAction(deleteConfirm.id)
    deleteConfirm.open = false
    await dicts.reload()
  } catch (e) {
    deleteConfirm.error = e?.response?.data?.error || e?.message || 'Ошибка удаления'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
/* ── Обёртка ───────────────────────────────────────────── */
.dicts-editor {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
}

/* ── Шапка ─────────────────────────────────────────────── */
.dicts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
  gap: 16px;
  flex-wrap: wrap;
}
.dicts-header__left   { display: flex; flex-direction: column; gap: 2px; }
.dicts-header__title  { font-size: 15px; font-weight: 700; color: #212121; }
.dicts-header__sub    { font-size: 12px; color: #9e9e9e; }

.dicts-subtabs { display: flex; gap: 6px; }
.subtab {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 16px; border-radius: 8px;
  border: 1.5px solid #e0e0e0;
  background: #fff; cursor: pointer;
  font-size: 13px; color: #546e7a;
  transition: all .18s; font-weight: 500;
}
.subtab:hover  { border-color: #90caf9; color: #1976d2; background: #f0f7ff; }
.subtab--active { border-color: #1976d2; background: #1976d2; color: #fff; }
.subtab--active .subtab__count { background: rgba(255,255,255,.25); color: #fff; }
.subtab__icon  { font-size: 14px; }
.subtab__count {
  background: #e3f2fd; color: #1565c0;
  border-radius: 10px; padding: 1px 7px;
  font-size: 11px; font-weight: 700;
  transition: all .18s;
}

/* ── Тело ──────────────────────────────────────────────── */
.dicts-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 24px;
}

/* ── Баннер-подсказка ───────────────────────────────────── */
.info-banner {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: #e8f4fd;
  border-left: 3px solid #42a5f5;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 12.5px;
  color: #1a237e;
  line-height: 1.6;
}
.info-banner--warn { background: #fff8e1; border-color: #f9a825; color: #424242; }
.info-banner__icon { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
.info-banner code {
  background: rgba(0,0,0,.06); color: inherit;
  padding: 1px 5px; border-radius: 3px; font-size: 11.5px;
}

/* ── Тулбар ────────────────────────────────────────────── */
.table-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}
.toolbar-error { font-size: 12px; color: #c62828; }

/* ── Таблица ───────────────────────────────────────────── */
.table-wrap {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
}
.dict-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.dict-table th {
  background: #f5f9ff;
  color: #1565c0;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .4px;
  padding: 10px 14px;
  text-align: left;
  border-bottom: 1.5px solid #dbe9fd;
}
.dict-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f5f5f5;
  color: #212121;
  vertical-align: middle;
}
.dict-table tr:last-child td { border-bottom: none; }
.dict-table tbody tr:hover td { background: #f8fbff; }

.code-cell {
  background: #e8f0fe;
  color: #1a237e;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 12px;
  font-family: 'Fira Code', monospace;
}
.desc-cell { color: #757575; font-size: 12.5px; }
.table-empty {
  text-align: center;
  color: #bdbdbd;
  padding: 28px !important;
  font-size: 13px;
}

/* Кнопки действий в строке */
.actions-cell { display: flex; gap: 6px; align-items: center; }
.act-btn {
  width: 30px; height: 30px;
  border: none; border-radius: 6px;
  cursor: pointer; font-size: 13px;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s;
}
.act-btn--edit { background: #e3f2fd; }
.act-btn--edit:hover { background: #bbdefb; }
.act-btn--del  { background: #fce4ec; }
.act-btn--del:hover  { background: #f8bbd9; }

/* ── Кнопки ────────────────────────────────────────────── */
.btn-add {
  display: inline-flex; align-items: center; gap: 6px;
  background: #1976d2; color: #fff;
  border: none; padding: 8px 18px; border-radius: 8px;
  cursor: pointer; font-size: 13px; font-weight: 600;
  transition: background .15s; box-shadow: 0 2px 6px rgba(25,118,210,.25);
}
.btn-add:hover { background: #1565c0; }

.btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  background: #1976d2; color: #fff;
  border: none; padding: 10px 22px; border-radius: 8px;
  cursor: pointer; font-size: 13px; font-weight: 600;
  transition: background .15s;
}
.btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.btn-primary:hover:not(:disabled) { background: #1565c0; }

.btn-danger {
  background: #d32f2f; color: #fff;
  border: none; padding: 10px 22px; border-radius: 8px;
  cursor: pointer; font-size: 13px; font-weight: 600;
  transition: background .15s;
}
.btn-danger:disabled { opacity: .6; }
.btn-danger:hover:not(:disabled) { background: #b71c1c; }

.btn-ghost {
  background: transparent; color: #546e7a;
  border: 1.5px solid #cfd8dc; padding: 10px 18px; border-radius: 8px;
  cursor: pointer; font-size: 13px;
  transition: all .15s;
}
.btn-ghost:hover { background: #f5f5f5; border-color: #b0bec5; }

.btn-spinner { display: inline-block; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Модалка ───────────────────────────────────────────── */
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, .45);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; backdrop-filter: blur(2px);
}
.modal {
  background: #fff;
  border-radius: 14px;
  width: 460px; max-width: 95vw;
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
  display: flex; flex-direction: column;
  overflow: hidden;
}
.modal--sm { width: 380px; }

.modal__header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 18px 22px 14px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}
.modal__title {
  display: flex; align-items: center; gap: 8px;
  font-size: 15px; font-weight: 700; color: #212121;
}
.modal__title-icon {
  width: 28px; height: 28px;
  background: #e3f2fd; border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px;
}
.modal__title-icon--danger { background: #fce4ec; }
.modal__close {
  background: none; border: none; font-size: 16px;
  cursor: pointer; color: #bdbdbd;
  width: 28px; height: 28px; border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s;
}
.modal__close:hover { background: #f5f5f5; color: #616161; }

.modal__body {
  padding: 20px 22px;
  display: flex; flex-direction: column; gap: 14px;
}
.modal__error {
  background: #ffebee; color: #c62828;
  padding: 10px 14px; border-radius: 8px;
  font-size: 13px; border-left: 3px solid #f44336;
}
.modal__footer {
  display: flex; gap: 10px;
  padding: 14px 22px 18px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

/* Поля формы */
.field { display: flex; flex-direction: column; gap: 4px; }
.field__label { font-size: 12px; font-weight: 600; color: #37474f; }
.field__req { color: #e53935; }
.field__hint { font-size: 11.5px; color: #9e9e9e; margin-top: -2px; }
.field__input {
  padding: 9px 12px;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  font-size: 13px;
  transition: border-color .15s;
  outline: none;
  box-sizing: border-box; width: 100%;
}
.field__input:focus { border-color: #1976d2; box-shadow: 0 0 0 3px rgba(25,118,210,.1); }
.field__input--disabled { background: #f5f5f5; color: #9e9e9e; }
.field__textarea {
  padding: 9px 12px;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  font-size: 13px;
  resize: vertical;
  outline: none; font-family: inherit;
  transition: border-color .15s;
  box-sizing: border-box; width: 100%;
}
.field__textarea:focus { border-color: #1976d2; box-shadow: 0 0 0 3px rgba(25,118,210,.1); }

.reminder {
  background: #fff3e0; border-left: 3px solid #f57c00;
  padding: 10px 13px; border-radius: 6px;
  font-size: 12px; color: #e65100; line-height: 1.6;
}
.reminder code { background: #ffe0b2; padding: 1px 4px; border-radius: 3px; font-size: 11px; }

/* Модалка удаления */
.delete-label { margin: 0; font-size: 13px; }
.delete-label code { background: #f5f5f5; padding: 2px 7px; border-radius: 4px; font-size: 12px; }
.delete-hint { margin: 6px 0 0; font-size: 12.5px; color: #757575; line-height: 1.6; }

/* ── Анимация модалки ───────────────────────────────────── */
.modal-enter-active, .modal-leave-active { transition: all .2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .modal, .modal-leave-to .modal { transform: scale(.95) translateY(8px); }
</style>
